/**
 * API connections — in-product reference.
 *
 * A right-side drawer opened from the API connections list (and linked from the
 * editor). It mirrors the partner integration guide
 * (backend/src/modules/http-automation/INTEGRATION-GUIDE.md) in customer-friendly
 * language: how to create a connection, the authentication types, request
 * headers, the signed customer-identity header (X-Baatly-User-Context), the
 * "User type" gate (Any / Specific / Verified), how a customer becomes Verified,
 * and how to map a response into variables.
 *
 * Content only — no data fetching. Keep it in sync with the markdown guide.
 */

import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Close,
  Webhook,
  VpnKey,
  Tune,
  DataObject,
  Badge,
  VerifiedUser,
  Code as CodeIcon,
  ListAlt,
} from '@mui/icons-material';

const ACCENT = '#1d6b4a';
const MONO = { fontFamily: 'monospace', fontSize: 12.5 } as const;

/** Inline monospace token, e.g. a header name or a field. */
const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="code"
    sx={{ ...MONO, bgcolor: 'action.hover', px: 0.6, py: 0.1, borderRadius: '5px', wordBreak: 'break-word' }}
  >
    {children}
  </Box>
);

/** A fenced code / example block. */
const Pre: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="pre"
    sx={{
      ...MONO,
      m: 0,
      mt: 1,
      p: 1.5,
      borderRadius: '10px',
      bgcolor: 'action.hover',
      border: 1,
      borderColor: 'divider',
      overflowX: 'auto',
      whiteSpace: 'pre',
      lineHeight: 1.65,
    }}
  >
    {children}
  </Box>
);

const Section: React.FC<{ id: string; icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  id,
  icon,
  title,
  children,
}) => (
  <Box id={id} sx={{ scrollMarginTop: 16, pt: 1 }}>
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: ACCENT, flexShrink: 0 }}>
        {icon}
      </Box>
      <Typography fontSize={17} fontWeight={700}>
        {title}
      </Typography>
    </Stack>
    <Box sx={{ color: 'text.secondary', fontSize: 14, lineHeight: 1.7, '& strong': { color: 'text.primary' } }}>
      {children}
    </Box>
  </Box>
);

/** A labelled definition row (two-column on wide, stacked on narrow). */
const DefRow: React.FC<{ term: React.ReactNode; children: React.ReactNode }> = ({ term, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: { xs: 0.25, sm: 2 }, py: 1, borderTop: 1, borderColor: 'divider' }}>
    <Typography fontSize={13.5} fontWeight={700} color="text.primary">
      {term}
    </Typography>
    <Box sx={{ fontSize: 13.5 }}>{children}</Box>
  </Box>
);

const TOC: { id: string; label: string }[] = [
  { id: 'g-overview', label: 'Overview' },
  { id: 'g-create', label: 'Create a connection' },
  { id: 'g-auth', label: 'Authentication' },
  { id: 'g-headers', label: 'Headers' },
  { id: 'g-identity', label: 'Customer-identity header' },
  { id: 'g-usertype', label: 'User type' },
  { id: 'g-verified', label: 'Verified users & step-up' },
  { id: 'g-map', label: 'Map the response' },
  { id: 'g-test', label: 'Test a connection' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

const ApiConnectionGuide: React.FC<Props> = ({ open, onClose }) => {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 680 }, maxWidth: '100%' } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 2 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: ACCENT }}>
          <Webhook fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize={18} fontWeight={700} lineHeight={1.2}>
            API connections — reference
          </Typography>
          <Typography variant="caption" color="text.secondary">
            How to connect an API, sign requests, and map what it returns.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close reference">
          <Close />
        </IconButton>
      </Box>

      {/* TOC */}
      <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {TOC.map((t) => (
          <Chip
            key={t.id}
            label={t.label}
            size="small"
            onClick={() => scrollTo(t.id)}
            sx={{ cursor: 'pointer', fontWeight: 500, bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
          />
        ))}
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, py: 2.5, overflowY: 'auto' }}>
        <Stack spacing={3.5} divider={<Divider flexItem />}>
          {/* ── Overview ─────────────────────────────────────────────── */}
          <Section id="g-overview" icon={<ListAlt fontSize="small" />} title="Overview">
            <p style={{ marginTop: 0 }}>
              An <strong>API connection</strong> is a reusable outbound HTTP request your automations can call. You
              define it once — the method, URL, authentication, and how to read the response — then drop a{' '}
              <strong>“Call API”</strong> step into any workflow to run it. Values the API returns become{' '}
              <strong>variables</strong> you can use later in the same conversation (reply text, a condition, a CRM field).
            </p>
            <p>
              Requests always go out over <strong>https/http</strong> to a <strong>public</strong> host. Private networks,{' '}
              <Code>localhost</Code>, and internal addresses are blocked for security — expose the endpoint through a public
              URL (or a tunnel) first.
            </p>
          </Section>

          {/* ── Create ───────────────────────────────────────────────── */}
          <Section id="g-create" icon={<Webhook fontSize="small" />} title="Create a connection">
            <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1 } }}>
              <li>
                <strong>New connection</strong> → give it a clear <strong>name</strong> (and an optional description).
              </li>
              <li>
                Pick the <strong>method</strong> (GET/POST/…) and paste the full <strong>URL</strong>, e.g.{' '}
                <Code>https://api.example.com/v1/orders</Code>. The host can’t contain a variable — only the path, query,
                headers and body can.
              </li>
              <li>
                Insert <strong>variables</strong> anywhere in the path, query params, headers or body with the{' '}
                <Code>{'{ }'}</Code> button, e.g. <Code>?phone={'{{contact.phone}}'}</Code>. They’re filled in at run time.
              </li>
              <li>
                Choose the <strong>authentication</strong> the API needs (see below).
              </li>
              <li>
                Hit <strong>Send test request</strong> to call the real endpoint and see the live response.
              </li>
              <li>
                <strong>Map the response</strong> — click fields in the result to save them as variables.
              </li>
              <li>
                <strong>Save.</strong> The connection is now selectable in a “Call API” workflow step.
              </li>
            </Box>
          </Section>

          {/* ── Authentication ───────────────────────────────────────── */}
          <Section id="g-auth" icon={<VpnKey fontSize="small" />} title="Authentication">
            <p style={{ marginTop: 0 }}>
              Two families: a <strong>public / third-party</strong> API (any endpoint, with whatever credential its owner
              gave you), or an <strong>external client</strong> (a system already connected to Baatly, where Baatly signs
              the request).
            </p>
            <Typography fontSize={13} fontWeight={700} color="text.primary" sx={{ mt: 1.5, mb: 0.5 }}>
              Public / third-party
            </Typography>
            <DefRow term="None">No credentials — a public endpoint (catalog, stock, store hours).</DefRow>
            <DefRow term="Bearer token">
              Sends <Code>Authorization: Bearer &lt;token&gt;</Code>. The most common REST style.
            </DefRow>
            <DefRow term="Basic">
              A <Code>user:password</Code> pair → <Code>Authorization: Basic …</Code>.
            </DefRow>
            <DefRow term="API key header">
              Your key under a header name you choose (default <Code>X-Api-Key</Code>).
            </DefRow>
            <DefRow term="HMAC signing">
              Signs the body: <Code>X-Webhook-Signature: t=…,v1=hmac(secret, t.body)</Code>. You supply the secret and the
              receiver verifies it.
            </DefRow>
            <DefRow term="OAuth2">
              Client-credentials grant — Baatly fetches and caches a token from your token URL, then sends it as a bearer.
            </DefRow>
            <Typography fontSize={13} fontWeight={700} color="text.primary" sx={{ mt: 2, mb: 0.5 }}>
              External client
            </Typography>
            <p style={{ marginTop: 0 }}>
              Pick a registered <strong>External API Client</strong> instead of entering a credential. Baatly signs every
              call with that client’s signing secret (the <strong>same scheme it uses for webhooks</strong>), so the
              partner verifies a Baatly signature rather than checking a token. Two rules apply: the URL must be on the
              client’s <strong>callback host</strong> (path can differ), and you enter <strong>no secret at all</strong>.
            </p>
            <Box sx={{ mt: 1, p: 1.25, borderRadius: '8px', bgcolor: 'action.hover' }}>
              <Typography fontSize={12.5} color="text.secondary">
                All secrets are <strong>write-only</strong> — once saved, the form only shows that a secret exists. Leave a
                secret field blank when editing to keep the stored value.
              </Typography>
            </Box>
          </Section>

          {/* ── Headers ──────────────────────────────────────────────── */}
          <Section id="g-headers" icon={<Tune fontSize="small" />} title="Headers">
            <p style={{ marginTop: 0 }}>
              Headers tell the API how to read your request and who’s calling. You control two layers, and Baatly adds a
              few automatically:
            </p>
            <DefRow term="Default headers">
              Connection-wide headers sent on every call (e.g. <Code>Accept: application/json</Code>).
            </DefRow>
            <DefRow term="Request headers">
              Per-request headers on the Headers tab. Values accept <Code>{'{{variables}}'}</Code> via the{' '}
              <Code>{'{ }'}</Code> button.
            </DefRow>
            <DefRow term="Content-Type">
              Set for you from the body mode (JSON / form / raw) — you don’t add it by hand.
            </DefRow>
            <DefRow term="Added by Baatly">
              The chosen auth header (<Code>Authorization</Code> / <Code>X-Api-Key</Code> / <Code>X-Webhook-Signature</Code>
              ), an <Code>Idempotency-Key</Code> on non-GET calls, and — if enabled — the signed{' '}
              <Code>X-Baatly-User-Context</Code> identity header.
            </DefRow>
            <Box sx={{ mt: 1, p: 1.25, borderRadius: '8px', border: 1, borderColor: 'warning.light', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,193,7,0.08)' : 'rgba(255,193,7,0.08)' }}>
              <Typography fontSize={12.5} color="text.secondary">
                <strong>Don’t put secrets in a header value or the URL query string</strong> — those are stored in plain
                text and visible to anyone editing automations. Use an Authentication type instead; it’s encrypted.
              </Typography>
            </Box>
          </Section>

          {/* ── Identity header ──────────────────────────────────────── */}
          <Section id="g-identity" icon={<Badge fontSize="small" />} title="Customer-identity header (X-Baatly-User-Context)">
            <p style={{ marginTop: 0 }}>
              When <strong>“Send a signed customer-identity header”</strong> is on, Baatly attaches a header that tells the
              receiving server <strong>which WhatsApp customer this call is for</strong> — in a form the server can verify
              and a flow author <strong>cannot forge</strong>.
            </p>
            <p>
              <strong>Why not just put <Code>?phone=</Code> in the URL?</strong> Baatly’s API credential is a single{' '}
              <em>service</em> credential. If a flow author (or injected text) could choose the phone number, that one
              credential could read <em>any</em> customer’s data. The signed header fixes this: it is server-asserted only
              (any author-supplied copy is stripped), HMAC-signed, expires in <strong>60 seconds</strong>, and is bound to
              the exact method + path + body with a single-use id (<Code>jti</Code>).
            </p>
            <Typography fontSize={13} fontWeight={700} color="text.primary" sx={{ mt: 1.5, mb: 0.25 }}>
              On the wire
            </Typography>
            <Pre>{`X-Baatly-User-Context: <base64url(payload)>.<hmac-hex>

payload = { sub, channel, tenantId, iat, exp = iat+60, jti, req }
  sub = the customer's WhatsApp number
  req = sha256( METHOD + "\\n" + path?query + "\\n" + sha256(body) )`}</Pre>
            <Typography fontSize={13} fontWeight={700} color="text.primary" sx={{ mt: 1.5, mb: 0.25 }}>
              The signing key
            </Typography>
            <DefRow term="Public / third-party">
              A <strong>dedicated</strong> “identity-header signing secret” you enter (never your API credential). Your
              server verifies with the same secret.
            </DefRow>
            <DefRow term="External client">
              Derived from the client’s signing secret — nothing extra to enter:
              <Pre>{`userContextKey = hmac_sha256( signingSecret, "baatly:user-context:v1" )  // hex`}</Pre>
            </DefRow>
            <p>
              Your server verifies by recomputing the key, checking the HMAC in constant time, checking <Code>exp</Code>,
              recomputing <Code>req</Code> for the request it received, and de-duplicating on <Code>jti</Code>. A full
              reference verifier (Node) is in the partner integration guide.
            </p>
            <Typography fontSize={13} fontWeight={700} color="text.primary" sx={{ mt: 1.5, mb: 0.25 }}>
              Do I still put the number in the URL / body?
            </Typography>
            <DefRow term="Header ON">
              <strong>No.</strong> The customer’s number rides in the signed header as <Code>sub</Code>; your API reads it
              from there. Don’t also add <Code>?phone=</Code> — that would be an unverified duplicate.
            </DefRow>
            <DefRow term="Header OFF">
              <strong>Yes</strong> — if the call is customer-specific, you must pass it yourself, e.g.{' '}
              <Code>?customer={'{{system.customerPhone}}'}</Code>, and the receiver simply trusts whatever’s in the URL
              (<em>not</em> verifiable). That’s exactly the weakness the header removes.
            </DefRow>
            <p style={{ marginBottom: 0 }}>
              So for “my orders / my bag”, turn the header <strong>on</strong> and drop the phone param — it’s both simpler
              and safer. (Your API must read <Code>sub</Code> from the verified header for this to work.)
            </p>
          </Section>

          {/* ── User type ────────────────────────────────────────────── */}
          <Section id="g-usertype" icon={<Tune fontSize="small" />} title="User type (who the call is about)">
            <p style={{ marginTop: 0 }}>
              Under <strong>Advanced settings</strong>, <strong>“User type”</strong> is a gate Baatly checks{' '}
              <em>before</em> the request goes out. If the conversation doesn’t have the required identity, the call is
              blocked and nothing leaves Baatly. It decides <em>who the data is about</em> — not who Baatly is.
            </p>
            <DefRow term={<Chip size="small" label="Any" sx={{ fontWeight: 600 }} />}>
              Store-wide data, not tied to a customer — catalog, stock, store hours, a generic lookup. The default.
            </DefRow>
            <DefRow term={<Chip size="small" label="Specific" sx={{ fontWeight: 600 }} />}>
              The chatting customer’s <strong>own</strong> data — “my orders”, “my bag”. Their identified WhatsApp number
              is proof enough, and it’s supplied <strong>automatically on every conversation</strong> (Meta signs the
              inbound message, so the number is trustworthy at message time).
            </DefRow>
            <DefRow term={<Chip size="small" label="Verified" sx={{ fontWeight: 600, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(29,107,74,0.25)' : 'rgba(29,107,74,0.12)', color: ACCENT }} />}>
              The customer additionally proved something (a PIN/OTP checked by <strong>your</strong> system). Required for
              sensitive or risky actions — payments, account changes, cancellations. Needs a <strong>“Mark Verified”</strong>{' '}
              step earlier in the flow (see below).
            </DefRow>
            <Box sx={{ mt: 1.25, p: 1.25, borderRadius: '8px', bgcolor: 'action.hover' }}>
              <Typography fontSize={12.5} color="text.secondary">
                <strong>User type</strong> (the gate) and <strong>the identity header</strong> (the claim) are independent.
                For a customer-specific call you usually want <strong>Specific</strong> + the header <strong>on</strong>, so
                the receiver both is allowed to run and learns — verifiably — who it’s for.
              </Typography>
            </Box>
          </Section>

          {/* ── Verified ─────────────────────────────────────────────── */}
          <Section id="g-verified" icon={<VerifiedUser fontSize="small" />} title="Who is a “Verified” user, and how to verify them">
            <p style={{ marginTop: 0 }}>
              <strong>Identified (“Specific”)</strong> means Meta has confirmed the person controls that WhatsApp number —
              automatic, every chat. That’s enough to <em>read</em> their own data, but it only proves{' '}
              <em>possession of the SIM at message time</em>. It does not protect against a shared phone, a stolen/unlocked
              device, or a SIM swap.
            </p>
            <p>
              <strong>Verified</strong> is a higher bar <em>you</em> opt into for sensitive actions. There is{' '}
              <strong>no built-in OTP</strong> — a code sent and read back in the same WhatsApp chat proves nothing beyond
              control of the number, which Meta already established. Instead you build real step-up from existing nodes and
              cap it with the <strong>“Mark Verified”</strong> node:
            </p>
            <Pre>{`collect_input   → ask the customer for a code / PIN
   ↓
Call API        → check it against YOUR verification endpoint
   ↓
condition       → did it pass?
   ↓  (yes)
Mark Verified   → raises this conversation to "Verified"
   ↓
Call API        → the connection whose User type = Verified`}</Pre>
            <p>
              The <strong>“Mark Verified”</strong> node doesn’t verify anything itself — it’s your explicit “verification
              passed” marker, placed on the success branch <em>after</em> your own check. Once it runs, any later{' '}
              <strong>Call API</strong> set to <strong>Verified</strong> is allowed, for the rest of that conversation
              (the level persists across a wait, up to 24 h). Until it runs, a Verified connection stays blocked.
            </p>
            <Box sx={{ mt: 0.5, p: 1.25, borderRadius: '8px', bgcolor: 'action.hover' }}>
              <Typography fontSize={12.5} color="text.secondary">
                So the real flow is: <strong>ask for a verification code → check the code against your system → on success,
                Mark Verified → the customer is now Verified.</strong> The code check is yours to own (an OTP you sent by
                SMS/email, an account PIN, account-linking) — anything stronger than “holds this SIM”.
              </Typography>
            </Box>
          </Section>

          {/* ── Map response ─────────────────────────────────────────── */}
          <Section id="g-map" icon={<DataObject fontSize="small" />} title="Map the response">
            <p style={{ marginTop: 0 }}>
              Mapping turns fields in the API’s JSON response into <strong>variables</strong> your flow can use. The easiest
              way: after a <strong>test request</strong>, click a value in the result — Baatly fills in its path and
              suggests a name.
            </p>
            <DefRow term="Source path">
              A dot/bracket path into the JSON, e.g. <Code>data[0].status</Code> or <Code>order.total</Code>.
            </DefRow>
            <DefRow term="Target">
              Where the value goes:
              <Box component="ul" sx={{ pl: 2.5, m: 0.5, '& li': { mb: 0.25 } }}>
                <li>
                  <strong>Variable</strong> — a <Code>{'{{flow variable}}'}</Code> usable in replies, conditions and later
                  steps this conversation.
                </li>
                <li>
                  <strong>Contact field</strong> / <strong>Lead field</strong> — saved onto the CRM record (persists).
                </li>
              </Box>
            </DefRow>
            <p style={{ marginBottom: 0 }}>
              Use a mapped value downstream as <Code>{'{{variableName}}'}</Code> — e.g. in a Send Response step:{' '}
              <Code>Your order is {'{{orderStatus}}'}.</Code> After the call you also get{' '}
              <Code>{'{{_httpStatus}}'}</Code> for branching on success/failure.
            </p>
          </Section>

          {/* ── Test ─────────────────────────────────────────────────── */}
          <Section id="g-test" icon={<CodeIcon fontSize="small" />} title="Test a connection">
            <p style={{ marginTop: 0 }}>
              <strong>Send test request</strong> calls the real endpoint and shows the status, body and timing — then you
              map fields from what comes back.
            </p>
            <p style={{ marginBottom: 0 }}>
              If the connection’s User type isn’t <strong>Any</strong> (or the identity header is on), a{' '}
              <strong>“Test as a customer”</strong> block appears: enter a sample number and pick a <strong>User type</strong>{' '}
              to simulate (<strong>Specific</strong> or <strong>Verified</strong>). A live conversation supplies this
              automatically; the test can’t, so you act as the customer. Pick a level that meets the connection’s
              requirement or the test is blocked — exactly as a real call would be.
            </p>
          </Section>
        </Stack>

        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Building the other side of the integration? The <strong>partner integration guide</strong> has the full
            signature spec and a reference verifier — ask your Baatly contact for it.
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ApiConnectionGuide;
